import {
  loadOrganisationPurchaseRequest,
  type OrganisationPurchaseRequestData,
  type OrganisationValidPurchaseRequestData,
  PURCHASE_REQUEST_DEFAULT_COLLECTION_DAYS,
  useAppService,
  useGetTradeSaleSummaryPreview,
  useGetTradeSaleTimelinePreview,
  useSelectedOrg,
  useSelectedOrgFeatures,
} from "@fero/hooks";
import type {
  CreatePurchaseRequestLineItem,
  PurchaseRequestPreviewReq,
} from "adl-gen/ferovinum/app/api";
import type { OrgPurchaseRequestSummaryPreview } from "adl-gen/ferovinum/app/nompurchaser";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatDateToISO8601 } from "utils/date-utils";
import { assertNotUndefined } from "utils/hx/util/types";
import { numberOfUnitsForUnitType } from "utils/model-utils";
import { ensure } from "utils/null-and-undefined-utils";
import { error, type LoadingValue, loaded, loading } from "utils/utility-types";
import type { ProductionPlanPurchaseRequest } from "./organisation-purchase-request-production-plan/organisation-purchase-request-production-plan-page-view";
import type { PurchaseRequestFlowState } from "./purchase-request-types";

export interface PurchaseRequestConfirmationData
  extends Omit<
    OrganisationPurchaseRequestData,
    | "cashAmountToAdvanceUponDelivery"
    | "summary"
    | "advanceUponDeliveryPaymentDate"
    | "orgNetReceivablePaymentDate"
  > {
  validEstimateNetReceivable: boolean;
  isConfirmed: boolean;
  productionPlan?: ProductionPlanPurchaseRequest;
  summary: OrgPurchaseRequestSummaryPreview;
  customerPoRef?: string;
  shipmentNumber?: string;
  advanceCashUponDelivery: boolean;
  netReceivableAmount: string;
  additionalMonthlyFeeAmount: string;
}

export const usePurchaseRequestPreview = (
  purchaseRequestFlowState: PurchaseRequestFlowState,
) => {
  const organisation = ensure(useSelectedOrg());
  const orgFeatures = assertNotUndefined(useSelectedOrgFeatures());
  const service = useAppService();
  const [purchaseRequestData, setPurchaseRequestData] = useState<
    LoadingValue<PurchaseRequestConfirmationData>
  >(loading());

  const delivery = ensure(purchaseRequestFlowState.delivery);
  const setup = ensure(purchaseRequestFlowState.setup);
  const settlement = ensure(purchaseRequestFlowState.settlement);
  const storageLocation = ensure(
    purchaseRequestFlowState.setup?.storageLocation,
  );
  const productionPlan = purchaseRequestFlowState.productionPlan;

  const hasValidNetReceivable = useCallback(
    (netReceivable: string): boolean => {
      return (
        Number(netReceivable) >= 0 ||
        orgFeatures.includes("allow_negative_receivable_trade_sales")
      );
    },
    [orgFeatures],
  );

  const purchaseRequestPreviewReq: PurchaseRequestPreviewReq = useMemo(() => {
    const buildLineItems = (): CreatePurchaseRequestLineItem[] =>
      setup.products.map((p) => {
        const productionOrders =
          productionPlan
            ?.find((plan) => plan.finishedProduct.product.id === p.product.id)
            ?.productionOrders.flatMap(({ sourceProduct, tasks }) =>
              tasks.map((task) => ({
                finishingProductMappingId: task.finishingProductMappingId,
                quantity: numberOfUnitsForUnitType(
                  sourceProduct.value.unitType,
                  task.quantity,
                ),
                pricePerUnit: task.pricePerUnit.toString(),
                setupCost: task.setupFee.toString(),
              })),
            ) ?? [];
        const attributeSelection = Object.entries(p.attributeSelection ?? {})
          .map(([_, selection]) => ({
            batchNumber: selection.batchNumber ?? null,
            bestBeforeDate: selection.bestBefore
              ? formatDateToISO8601(selection.bestBefore)
              : null,
            rotationNumber: selection.rotationNumber ?? null,
            rotationLine: selection.rotationLine ?? null,
            quantity: selection.quantity,
          }))
          .filter((item) => Number(item.quantity) > 0);

        return {
          productId: p.product.id,
          quantity: numberOfUnitsForUnitType(
            p.product.value.unitType,
            p.paidUnits,
          ),
          freeUnits: p.freeUnits
            ? numberOfUnitsForUnitType(p.product.value.unitType, p.freeUnits)
            : null,
          unitPrice: p.unitPrice.toString(),
          priceDiscount: p.priceDiscount?.toString() ?? null,
          fromProductionOrders: productionOrders,
          attributeSelection: attributeSelection,
        };
      });

    return {
      nominatedPurchaserId: setup.purchaser.nominatedPurchaserId,
      storageLocationId: storageLocation.id,
      stockPreparationDays: delivery.preparationDays,
      purchaseRequestDeliveryOption: delivery.deliveryOption,
      paymentTermsPeriod: settlement.paymentTermsPeriod,
      purchaserCoversDeliveryCost: delivery.purchaserCoversDeliveryCost,
      salePriceType: setup.salePriceType,
      lineItems: buildLineItems(),
      dealDiscountAmount: setup.dealDiscountAmount ?? null,
      customDeliveryCost: delivery.customDeliveryCost ?? null,
      additionalDeliveryCost: delivery.additionalDeliveryCost ?? null,
    };
  }, [delivery, productionPlan, settlement, setup, storageLocation]);

  const summaryPreview = useGetTradeSaleSummaryPreview(
    purchaseRequestPreviewReq,
  );
  const timelinePreview = useGetTradeSaleTimelinePreview({
    ...purchaseRequestPreviewReq,
    paymentTerms: settlement.paymentTermsPeriod,
    deliveryOption: delivery.deliveryOption,
  });

  const loadPurchaseRequestData = useCallback(
    async (
      purchaseRequestId?: string,
    ): Promise<LoadingValue<PurchaseRequestConfirmationData>> => {
      const calculateAmounts = (summary: OrgPurchaseRequestSummaryPreview) => ({
        netReceivableAmount: settlement.advanceCashUponDelivery
          ? (summary.discountedReceivable?.netReceivable ??
            summary.netReceivable)
          : summary.netReceivable,
        additionalMonthlyFeeAmount: settlement.advanceCashUponDelivery
          ? (summary.discountedReceivable?.additionalMonthlyFees ??
            summary.additionalMonthlyFees)
          : summary.additionalMonthlyFees,
      });

      if (purchaseRequestId) {
        const confirmed = (await loadOrganisationPurchaseRequest(
          service,
          purchaseRequestId,
        )) as OrganisationValidPurchaseRequestData;
        const summary: OrgPurchaseRequestSummaryPreview = {
          ...confirmed.summary,
          dealDiscountAmount: confirmed.summary.dealDiscountAmount,
          discountedReceivable: confirmed.summary
            .cashAmountToAdvanceUponDelivery
            ? {
                ...confirmed.summary,
                amountAdvancedUponDelivery:
                  confirmed.summary.cashAmountToAdvanceUponDelivery ?? "0",
              }
            : null,
        };
        const { netReceivableAmount, additionalMonthlyFeeAmount } =
          calculateAmounts(summary);
        return loaded({
          ...confirmed,
          advanceCashUponDelivery: settlement.advanceCashUponDelivery,
          customerPoRef: confirmed.summary.customerPoRef ?? undefined,
          shipmentNumber: confirmed.summary.shipmentNumber ?? undefined,
          isConfirmed: true,
          validEstimateNetReceivable: true,
          productionPlan,
          summary,
          netReceivableAmount,
          additionalMonthlyFeeAmount,
        });
      }

      if (summaryPreview.isLoading || timelinePreview.isLoading) {
        return loading();
      }

      if (summaryPreview.isError || timelinePreview.isError) {
        return error(new Error("Unable to load trade sale preview"));
      }
      const summary = summaryPreview.data;
      const timeline = timelinePreview.data;
      if (summary.kind === "invalidPurchaseRequest") {
        throw new Error("Purchase request is rejected");
      }

      const { netReceivableAmount, additionalMonthlyFeeAmount } =
        calculateAmounts(summary.value);
      return loaded({
        isConfirmed: false,
        state: "new",
        stateEvents: [],
        purchaseRequestNumber: "",
        purchaserCoversDeliveryCost: delivery.purchaserCoversDeliveryCost,
        purchaserCurrency: setup.purchaser.currency,
        settlementCurrency: organisation.value.currency,
        stockPreparationDays: delivery.preparationDays,
        collectionDays: PURCHASE_REQUEST_DEFAULT_COLLECTION_DAYS,
        paymentTermsPeriod: settlement.paymentTermsPeriod,
        storageLocation,
        purchaserName: setup.purchaser.name,
        purchaseRequestDeliveryOption: delivery.deliveryOption,
        deliveryStatuses: [],
        terms: setup.purchaser.terms,
        validEstimateNetReceivable: hasValidNetReceivable(netReceivableAmount),
        productionPlan: productionPlan,
        summary: summary.value,
        timeline,
        salePriceType: setup.salePriceType,
        poFileId: null,
        deliveryInstructionDocs: null,
        collectedDate: null,
        deliveredDate: null,
        purchaserInvoiceId: null,
        attachedDocuments: [],
        advanceCashUponDelivery: settlement.advanceCashUponDelivery,
        netReceivableAmount,
        additionalMonthlyFeeAmount,
        rechargeDeliveryFee: summary.value.rechargeDeliveryFee,
        rechargeTaxes: summary.value.rechargeTaxes,
      });
    },
    [
      summaryPreview.isLoading,
      summaryPreview.isError,
      summaryPreview.data,
      timelinePreview.isLoading,
      timelinePreview.isError,
      timelinePreview.data,
      storageLocation,
      delivery.deliveryOption,
      delivery.purchaserCoversDeliveryCost,
      delivery.preparationDays,
      setup.purchaser.currency,
      setup.purchaser.name,
      setup.purchaser.terms,
      setup.salePriceType,
      organisation.value.currency,
      settlement.paymentTermsPeriod,
      settlement.advanceCashUponDelivery,
      hasValidNetReceivable,
      productionPlan,
      service,
    ],
  );

  useEffect(() => {
    loadPurchaseRequestData()
      .then(setPurchaseRequestData)
      .catch((error) =>
        setPurchaseRequestData({
          state: "error",
          error: error as Error,
        }),
      );
  }, [loadPurchaseRequestData]);

  const reloadPurchaseRequestData = useCallback(
    (purchaseRequestId?: string) => {
      loadPurchaseRequestData(purchaseRequestId)
        .then(setPurchaseRequestData)
        .catch((error) =>
          setPurchaseRequestData({
            state: "error",
            error: error as Error,
          }),
        );
    },
    [loadPurchaseRequestData],
  );

  return {
    purchaseRequestData,
    loadPurchaseRequestData: reloadPurchaseRequestData,
    purchaseRequestPreviewReq,
  };
};
